// @ts-nocheck
/**
 * 装備効果の日本語説明文マッピング
 *
 * 効果タイプから人間が読める日本語テキストを生成する
 *
 * ===== 重要 =====
 * 効果タイプは recipes.js (crafting_system_design.md) に準拠
 * equipmentData.js の EQUIPMENT_EFFECT_TYPES は非推奨
 */

/**
 * 効果タイプの基本説明
 */
export const EFFECT_TYPE_NAMES = {
  // ===== recipes.js で使用される効果タイプ（正式） =====

  // 属性系（attribute_）
  attribute_resistance: '属性耐性',
  attribute_damage_boost: '属性ダメージ強化',
  attribute_killer: '属性特効',

  // カテゴリ・派閥系
  category_boost: 'カテゴリ強化',
  faction_boost: '派閥強化',

  // ステータス系
  atk_percent: '攻撃力%',
  hp_percent: 'HP%',
  all_stats: '全ステータス',

  // クリティカル系
  critical: 'クリティカル率',
  critical_boost: 'クリティカルダメージ',

  // スキル系
  skill_damage: 'スキルダメージ',
  cooldown_reduction: 'クールダウン短縮',

  // 回復・吸収系
  heal_boost: '回復効果強化',
  hp_drain: 'HP吸収',

  // 回避・先制系
  evasion: '回避',
  preemptive_chance: '先制確率',
  guaranteed_first: '先制確定',

  // 状態異常付与
  burn_inflict: '火傷付与',
  poison_inflict: '毒付与',
  poison_damage: '毒ダメージ',
  paralyze_inflict: '麻痺付与',
  stun_chance: 'スタン確率',

  // 状態異常耐性
  instant_death_resistance: '即死耐性',
  instant_death_inflict: '即死付与',

  // 特殊効果
  split_chance: '分裂確率',
  control_success: '操作成功率',
  debuff_power: 'デバフ効果',
  forbidden_power: '禁忌スキル威力',

  // 周回系
  exp_boost: '経験値',
  gold_boost: 'ゴールド',
  drop_rate_boost: 'ドロップ率',
  treasure_find_boost: '宝箱発見',

  // ===== 旧効果タイプ（互換性のため残す） =====

  // ダメージ系
  attribute_damage: '属性ダメージ',
  category_damage: 'カテゴリ特効',
  dark_slayer: '闇特効',

  // 火傷系
  burn_chance: '火傷付与',
  burn_damage_boost: '火傷ダメージ',
  burn_immune: '火傷無効',
  all_fire_boost: '炎属性強化',
  fire_resistance: '炎耐性',
  fire_immunity: '炎無効',

  // 水・凍結系
  all_water_boost: '水属性強化',
  water_resistance: '水耐性',
  water_absorption: '水吸収',
  freeze_chance: '凍結付与',
  freeze_immunity: '凍結無効',
  wet_application: '濡れ付与',
  heal_on_turn: 'ターン回復',
  heal_received_boost: '被回復効果',
  tidal_blessing: '潮流の祝福',

  // 光・浄化系
  purify_chance: '浄化',
  purify_self_chance: '自己浄化',
  dark_resistance: '闇耐性',
  debuff_resistance: 'デバフ耐性',
  blessing_aura: '祝福のオーラ',
  ally_heal_boost: '味方回復効果',
  revival_boost: '蘇生効果',

  // 闇・呪い系
  curse_chance: '呪い付与',
  curse_duration_boost: '呪い延長',
  debuff_boost: 'デバフ強化',
  debuff_immunity: 'デバフ耐性',
  life_steal: '吸血',
  execute_threshold: '処刑',

  // 原始系
  primal_resistance: '原始耐性',
  armor_break: '防御貫通',
  regeneration: 'HP再生',
  split_on_death: '死亡時分裂',

  // 未来系
  action_speed_boost: '行動速度',
  pierce_defense: '防御無視',
  dimension_shift: '次元転移',
  experience_boost: '経験値',
  skill_memory: 'スキル記憶',

  // 回避・クリティカル系
  stealth_strike: '奇襲',
  crit_damage_boost: '会心ダメージ',

  // 防御・耐久系
  damage_reduction: 'ダメージ軽減',
  energy_shield: 'エネルギーシールド',

  // 蘇生・不死系
  phoenix_rebirth: '不死鳥再生',
  auto_revive: '自動蘇生',

  // 特殊効果
  skill_power_boost: 'スキル威力',
  forbidden_knowledge: '禁忌の知識',
};

/**
 * 効果オブジェクトから日本語説明文を生成
 *
 * @param {Object} effect - 効果オブジェクト
 * @returns {string} 日本語説明文
 */
export function getEffectDescription(effect) {
  if (!effect || !effect.type) {
    return '不明な効果';
  }

  const { type } = effect;

  // ===== recipes.js で使用される効果タイプ（正式） =====

  // 属性耐性（○属性ダメージ-○%）
  if (type === 'attribute_resistance') {
    const attr = effect.attribute || '属性';
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `${attr}ダメージ-${value}%`;
  }

  // 属性ダメージ強化（○属性ダメージ+○%）
  if (type === 'attribute_damage_boost') {
    const attr = effect.attribute || '属性';
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `${attr}属性ダメージ+${value}%`;
  }

  // 属性特効（対○属性+○%）
  if (type === 'attribute_killer') {
    const attr = effect.attribute || '属性';
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `対${attr}属性+${value}%`;
  }

  // カテゴリ強化（○系能力+○%）
  if (type === 'category_boost') {
    const category = effect.category || 'カテゴリ';
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `${category}能力+${value}%`;
  }

  // 派閥強化（○能力+○%）
  if (type === 'faction_boost') {
    const faction = effect.faction || '派閥';
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `${faction}能力+${value}%`;
  }

  // 攻撃力%
  if (type === 'atk_percent') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `ATK+${value}%`;
  }

  // HP%
  if (type === 'hp_percent') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `HP+${value}%`;
  }

  // 全ステータス
  if (type === 'all_stats') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `全ステータス+${value}%`;
  }

  // クリティカル率
  if (type === 'critical') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `クリティカル率+${value}%`;
  }

  // クリティカルダメージ
  if (type === 'critical_boost') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `クリティカルダメージ+${value}%`;
  }

  // スキルダメージ
  if (type === 'skill_damage') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `スキルダメージ+${value}%`;
  }

  // 経験値ブースト
  if (type === 'exp_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `EXP+${bonus}%`;
  }

  // ゴールドブースト
  if (type === 'gold_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `ゴールド+${bonus}%`;
  }

  // ドロップ率ブースト
  if (type === 'drop_rate_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `ドロップ率+${bonus}%`;
  }

  // 宝箱発見ブースト
  if (type === 'treasure_find_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `宝箱発見+${bonus}%`;
  }

  // 先制確率
  if (type === 'preemptive_chance') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `先制確率+${value}%`;
  }

  // 先制確定
  if (type === 'guaranteed_first') {
    return '先制確定';
  }

  // 火傷付与
  if (type === 'burn_inflict') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `火傷付与+${value}%`;
  }

  // 毒付与
  if (type === 'poison_inflict') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `毒付与+${value}%`;
  }

  // 毒ダメージ
  if (type === 'poison_damage') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `毒ダメージ+${value}%`;
  }

  // 麻痺付与
  if (type === 'paralyze_inflict') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `麻痺付与+${value}%`;
  }

  // スタン確率
  if (type === 'stun_chance') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `スタン確率+${value}%`;
  }

  // 即死耐性
  if (type === 'instant_death_resistance') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `即死耐性+${value}%`;
  }

  // 即死付与
  if (type === 'instant_death_inflict') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `即死付与+${value}%`;
  }

  // 分裂確率
  if (type === 'split_chance') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `分裂確率+${value}%`;
  }

  // 操作成功率
  if (type === 'control_success') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `操作成功率+${value}%`;
  }

  // デバフ効果
  if (type === 'debuff_power') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `デバフ効果+${value}%`;
  }

  // 禁忌スキル威力
  if (type === 'forbidden_power') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `禁忌スキル威力+${value}%`;
  }

  // HP吸収
  if (type === 'hp_drain') {
    const value = effect.value ? Math.round(effect.value * 100) : 0;
    return `HP吸収+${value}%`;
  }

  // ===== 旧効果タイプ（互換性のため残す） =====

  // 属性ダメージボーナス
  if (type === 'attribute_damage') {
    const attr = effect.attribute || '属性';
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `${attr}属性ダメージ+${bonus}%`;
  }

  // カテゴリ特効
  if (type === 'category_damage') {
    const category = effect.category || 'カテゴリ';
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `${category}へのダメージ+${bonus}%`;
  }

  // 闇特効
  if (type === 'dark_slayer') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `闇属性へのダメージ+${bonus}%`;
  }

  // 火傷付与確率
  if (type === 'burn_chance') {
    const chance = effect.chance ? Math.round(effect.chance * 100) : 0;
    return `攻撃時${chance}%で火傷付与`;
  }

  // 火傷ダメージ増加
  if (type === 'burn_damage_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `火傷ダメージ+${bonus}%`;
  }

  // 火傷無効
  if (type === 'burn_immune') {
    return '火傷状態にならない';
  }

  // 炎属性全体強化
  if (type === 'all_fire_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `炎属性攻撃+${bonus}%`;
  }

  // 炎耐性
  if (type === 'fire_resistance') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `炎属性ダメージ-${bonus}%`;
  }

  // 炎無効
  if (type === 'fire_immunity') {
    return '炎属性ダメージ無効';
  }

  // 不死鳥再生
  if (type === 'phoenix_rebirth') {
    const cd = effect.cooldown || 0;
    return `致死ダメージ時HP1で復活（${cd}ターンCD）`;
  }

  // 水属性全体強化
  if (type === 'all_water_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `水属性攻撃+${bonus}%`;
  }

  // 水耐性
  if (type === 'water_resistance') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `水属性ダメージ-${bonus}%`;
  }

  // 水吸収
  if (type === 'water_absorption') {
    const percent = effect.percent ? Math.round(effect.percent * 100) : 0;
    return `水属性攻撃を${percent}%吸収`;
  }

  // 凍結付与
  if (type === 'freeze_chance') {
    const chance = effect.chance ? Math.round(effect.chance * 100) : 0;
    return `攻撃時${chance}%で凍結付与`;
  }

  // 凍結無効
  if (type === 'freeze_immunity') {
    return '凍結状態にならない';
  }

  // 濡れ付与
  if (type === 'wet_application') {
    const chance = effect.chance ? Math.round(effect.chance * 100) : 0;
    return `攻撃時${chance}%で濡れ付与`;
  }

  // 回復効果強化
  if (type === 'heal_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `回復効果+${bonus}%`;
  }

  // 毎ターン回復
  if (type === 'heal_on_turn') {
    const percent = effect.percent ? Math.round(effect.percent * 100) : 0;
    return `毎ターンHP${percent}%回復`;
  }

  // 被回復効果
  if (type === 'heal_received_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `受ける回復+${bonus}%`;
  }

  // 潮流の祝福
  if (type === 'tidal_blessing') {
    return '味方全体の水属性を強化';
  }

  // 浄化
  if (type === 'purify_chance') {
    const chance = effect.chance ? Math.round(effect.chance * 100) : 0;
    return `攻撃時${chance}%で敵のバフ解除`;
  }

  // 自己浄化
  if (type === 'purify_self_chance') {
    const chance = effect.chance ? Math.round(effect.chance * 100) : 0;
    return `ターン開始時${chance}%でデバフ解除`;
  }

  // 闇耐性
  if (type === 'dark_resistance') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `闇属性ダメージ-${bonus}%`;
  }

  // デバフ耐性
  if (type === 'debuff_resistance') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `デバフ耐性+${bonus}%`;
  }

  // 祝福のオーラ
  if (type === 'blessing_aura') {
    return '味方全体に祝福付与';
  }

  // 味方回復強化
  if (type === 'ally_heal_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `味方への回復+${bonus}%`;
  }

  // 蘇生効果強化
  if (type === 'revival_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `蘇生時HP+${bonus}%`;
  }

  // 呪い付与
  if (type === 'curse_chance') {
    const chance = effect.chance ? Math.round(effect.chance * 100) : 0;
    return `攻撃時${chance}%で呪い付与`;
  }

  // 呪い延長
  if (type === 'curse_duration_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `呪い持続時間+${bonus}%`;
  }

  // デバフ効果強化
  if (type === 'debuff_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `デバフ効果+${bonus}%`;
  }

  // デバフ無効
  if (type === 'debuff_immunity') {
    const percent = effect.percent ? Math.round(effect.percent * 100) : 0;
    return `${percent}%でデバフ無効`;
  }

  // 吸血
  if (type === 'life_steal') {
    const percent = effect.percent ? Math.round(effect.percent * 100) : 0;
    return `与ダメの${percent}%をHP回復`;
  }

  // 処刑
  if (type === 'execute_threshold') {
    const percent = effect.percent ? Math.round(effect.percent * 100) : 0;
    return `敵HP${percent}%以下で即死`;
  }

  // 原始耐性
  if (type === 'primal_resistance') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `原始属性ダメージ-${bonus}%`;
  }

  // 気絶付与
  if (type === 'stun_chance') {
    const chance = effect.chance ? Math.round(effect.chance * 100) : 0;
    return `攻撃時${chance}%で気絶付与`;
  }

  // 防御貫通
  if (type === 'armor_break') {
    const percent = effect.percent ? Math.round(effect.percent * 100) : 0;
    return `敵の防御${percent}%無視`;
  }

  // HP再生
  if (type === 'regeneration') {
    const percent = effect.percent ? Math.round(effect.percent * 100) : 0;
    return `毎ターンHP${percent}%回復`;
  }

  // 死亡時分裂
  if (type === 'split_on_death') {
    const count = effect.count || 2;
    const hp = effect.hpPercent ? Math.round(effect.hpPercent * 100) : 30;
    return `死亡時HP${hp}%の分身を${count}体生成`;
  }

  // CD短縮
  if (type === 'cooldown_reduction') {
    const percent = effect.percent ? Math.round(effect.percent * 100) : 0;
    return `スキルCD-${percent}%`;
  }

  // 行動速度
  if (type === 'action_speed_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `行動速度+${bonus}%`;
  }

  // 防御無視
  if (type === 'pierce_defense') {
    const percent = effect.percent ? Math.round(effect.percent * 100) : 0;
    return `防御${percent}%無視`;
  }

  // 次元転移
  if (type === 'dimension_shift') {
    const cd = effect.cooldown || 0;
    return `ダメージ無効化（${cd}ターンCD）`;
  }

  // 経験値増加
  if (type === 'experience_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `獲得経験値+${bonus}%`;
  }

  // スキル記憶
  if (type === 'skill_memory') {
    return '直前のスキルを記憶し再使用可能';
  }

  // 回避
  if (type === 'evasion') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `回避率+${bonus}%`;
  }

  // 奇襲
  if (type === 'stealth_strike') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `先制攻撃時ダメージ+${bonus}%`;
  }

  // 会心ダメージ
  if (type === 'crit_damage_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `会心ダメージ+${bonus}%`;
  }

  // 固定ダメージ軽減
  if (type === 'damage_reduction') {
    const flat = effect.flat || 0;
    return `受けるダメージ-${flat}`;
  }

  // エネルギーシールド
  if (type === 'energy_shield') {
    const value = effect.value || 0;
    return `${value}ダメージを吸収するシールド`;
  }

  // 自動蘇生
  if (type === 'auto_revive') {
    const hp = effect.hpPercent ? Math.round(effect.hpPercent * 100) : 30;
    const cd = effect.cooldown || 0;
    return `戦闘不能時HP${hp}%で復活（${cd}ターンCD）`;
  }

  // スキル威力
  if (type === 'skill_power_boost') {
    const bonus = effect.bonus ? Math.round(effect.bonus * 100) : 0;
    return `スキル威力+${bonus}%`;
  }

  // HP消費
  if (type === 'hp_drain') {
    const percent = effect.percent ? Math.round(effect.percent * 100) : 0;
    return `毎ターンHP${percent}%消費`;
  }

  // 禁忌の知識
  if (type === 'forbidden_knowledge') {
    return '禁忌カードのスキル威力+50%';
  }

  // デフォルト: タイプ名をそのまま表示
  return EFFECT_TYPE_NAMES[type] || type;
}

/**
 * 効果配列から説明文リストを生成
 *
 * @param {Object[]} effects - 効果オブジェクト配列
 * @returns {string[]} 説明文配列
 */
export function getEffectDescriptions(effects) {
  if (!effects || !Array.isArray(effects)) {
    return [];
  }
  return effects.map(getEffectDescription);
}

export default {
  EFFECT_TYPE_NAMES,
  getEffectDescription,
  getEffectDescriptions,
};
